import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
  Spin,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Space,
  Button,
  Typography,
  Table,
  Tag,
  Segmented,
  Empty,
  Avatar,
} from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  PieChartOutlined,
  BarChartOutlined,
  TableOutlined,
  DownloadOutlined,
  UserOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthContext";
import dayjs from "dayjs";

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const STATUS_COLORS = {
  "PRESENT": "#10b981",
  "LATE": "#d97706",
  "LEAVE": "#ef4444",
  "PERMISSION": "#2563eb",
  "ABSENT": "#64748b",
  "SUNDAY": "#94a3b8",
  "HOLIDAY": "#a855f7",
};

export default function EmployeeAttendance() {
  const { authData, backUrl } = useContext(AuthContext) || { backUrl: "" };

  const [rawEmployees, setRawEmployees] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [organisationId, setOrganisationId] = useState("ALL");

  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState("");

  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [searchTerm, setSearchTerm] = useState("");

  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("table"); // 'table' | 'chart'
  const [chartType, setChartType] = useState("bar"); // 'bar' | 'pie'

  // Fetch Organisations
  useEffect(() => {
    const fetchOrganisation = async () => {
      try {
        const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
          headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setOrganisations(res.data.results || res.data || []);
      } catch (err) {
        console.error("Error fetching organisations:", err);
      }
    };
    fetchOrganisation();
  }, [backUrl, authData?.access]);

  // Fetch Departments when Organisation changes
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const orgParam = organisationId !== "ALL" ? `?organisation=${organisationId}` : "";
        const res = await axios.get(`${backUrl}/api/emp/departments/${orgParam}`, {
          headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setDepartments(res.data.results || res.data || []);
        setDepartmentId("");
      } catch (err) {
        console.error("Error fetching departments:", err);
      }
    };
    fetchDepartments();
  }, [organisationId, backUrl, authData?.access]);

  // Fetch Attendance Data for Selected Date
  useEffect(() => {
    const fetchAttendance = async () => {
      setLoading(true);
      try {
        const targetDate = selectedDate ? selectedDate.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
        const params = {
          from_date: targetDate,
          to_date: targetDate,
        };
        if (organisationId !== "ALL") params.organisation = organisationId;
        if (departmentId) params.department = departmentId;

        const res = await axios.get(
          `${backUrl}/api/hr/employee-attendance-summary/`,
          {
            params,
            headers: { Authorization: `Bearer ${authData?.access}` },
          }
        );

        const list = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setRawEmployees(list);
      } catch (err) {
        console.error("Error loading attendance summary:", err);
        setRawEmployees([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendance();
  }, [organisationId, departmentId, selectedDate, backUrl, authData?.access]);

  // Process Employees for Display
  const processedEmployees = rawEmployees.map((emp) => {
    const attList = Array.isArray(emp.attendance) ? emp.attendance : [];
    const todayAtt = attList[0] || {};
    let rawStatus = (todayAtt.status || "").toUpperCase();
    let firstInType = (todayAtt.first_in_type || "").toUpperCase();
    let statusMsg = (todayAtt.status_message || "").toUpperCase();

    let statusKey = "ABSENT";
    let statusLabel = "Absent";
    let statusColor = "default";

    if (rawStatus.includes("LATE") || firstInType.includes("LATE") || statusMsg.includes("LATE")) {
      statusKey = "LATE";
      statusLabel = "Late In";
      statusColor = "warning";
    } else if (rawStatus.includes("PRESENT") || todayAtt.check_in) {
      statusKey = "PRESENT";
      statusLabel = "Present";
      statusColor = "green";
    } else if (rawStatus.includes("LEAVE")) {
      statusKey = "LEAVE";
      statusLabel = "On Leave";
      statusColor = "error";
    } else if (rawStatus.includes("PERMISSION")) {
      statusKey = "PERMISSION";
      statusLabel = "Permission";
      statusColor = "geekblue";
    } else if (rawStatus.includes("SUNDAY")) {
      statusKey = "SUNDAY";
      statusLabel = "Sunday";
      statusColor = "blue";
    } else if (rawStatus.includes("HOLIDAY")) {
      statusKey = "HOLIDAY";
      statusLabel = "Holiday";
      statusColor = "purple";
    }

    // Format times
    let checkInStr = todayAtt.check_in || "-";
    if (checkInStr !== "-" && checkInStr.length >= 5) {
      try {
        const parts = checkInStr.split(":");
        let h = parseInt(parts[0], 10);
        const m = parts[1];
        const ampm = h >= 12 ? "PM" : "AM";
        h = h % 12 || 12;
        checkInStr = `${h}:${m} ${ampm}`;
      } catch (e) {
        checkInStr = todayAtt.check_in;
      }
    }

    let checkOutStr = todayAtt.check_out || "-";
    if (checkOutStr !== "-" && checkOutStr.length >= 5) {
      try {
        const parts = checkOutStr.split(":");
        let h = parseInt(parts[0], 10);
        const m = parts[1];
        const ampm = h >= 12 ? "PM" : "AM";
        h = h % 12 || 12;
        checkOutStr = `${h}:${m} ${ampm}`;
      } catch (e) {
        checkOutStr = todayAtt.check_out;
      }
    }

    return {
      id: emp.employee_id,
      code: emp.employee_code || "-",
      name: emp.employee_name || "Employee",
      photo: emp.photo,
      department: emp.department || "-",
      designation: emp.designation || "-",
      organisation: emp.organisation || "-",
      statusKey,
      statusLabel,
      statusColor,
      checkIn: checkInStr,
      checkOut: checkOutStr,
      totalTime: todayAtt.total_time || "-",
    };
  });

  // Calculate Overall Live Metric Counts
  const totalCount = processedEmployees.length;
  const presentCount = processedEmployees.filter((e) => e.statusKey === "PRESENT").length;
  const lateCount = processedEmployees.filter((e) => e.statusKey === "LATE").length;
  const leaveCount = processedEmployees.filter((e) => e.statusKey === "LEAVE").length;
  const permissionCount = processedEmployees.filter((e) => e.statusKey === "PERMISSION").length;
  const absentCount = processedEmployees.filter((e) => e.statusKey === "ABSENT").length;
  const sundayCount = processedEmployees.filter((e) => e.statusKey === "SUNDAY").length;
  const holidayCount = processedEmployees.filter((e) => e.statusKey === "HOLIDAY").length;

  // Filtered List based on Search & Status Dropdown
  const filteredEmployees = processedEmployees.filter((emp) => {
    const matchesSearch =
      (emp.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (emp.code || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (emp.department || "").toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      selectedStatus === "ALL" || emp.statusKey === selectedStatus;

    return matchesSearch && matchesStatus;
  });

  // Department-wise Aggregation for Bar Chart
  const deptMap = {};
  processedEmployees.forEach((emp) => {
    const dName = emp.department || "Other";
    if (!deptMap[dName]) {
      deptMap[dName] = { department: dName, Present: 0, Late: 0, Leave: 0, Absent: 0 };
    }
    if (emp.statusKey === "PRESENT") deptMap[dName].Present++;
    else if (emp.statusKey === "LATE") deptMap[dName].Late++;
    else if (emp.statusKey === "LEAVE") deptMap[dName].Leave++;
    else if (emp.statusKey === "ABSENT") deptMap[dName].Absent++;
  });
  const deptChartData = Object.values(deptMap);

  const pieChartData = [
    { name: "Present", value: presentCount },
    { name: "Late In", value: lateCount },
    { name: "On Leave", value: leaveCount },
    { name: "Permission", value: permissionCount },
    { name: "Absent", value: absentCount },
  ].filter((i) => i.value > 0);

  const PIE_COLORS = ["#10b981", "#d97706", "#ef4444", "#2563eb", "#64748b"];

  // Table Columns
  const tableColumns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 90,
      render: (v) => <Tag style={{ fontWeight: 700, borderRadius: 6, margin: 0 }}>{v || "-"}</Tag>,
    },
    {
      title: "Employee Name",
      dataIndex: "name",
      key: "name",
      render: (v, r) => (
        <Space size="small">
          <Avatar
            size="small"
            src={r.photo}
            icon={!r.photo && <UserOutlined />}
            style={{ backgroundColor: "#0e497b" }}
          />
          <Text style={{ fontWeight: 700, color: "#0f172a" }}>{v}</Text>
        </Space>
      ),
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
      render: (v) => <span style={{ color: "#64748b", fontSize: 12.5 }}>{v}</span>,
    },
    {
      title: "Designation",
      dataIndex: "designation",
      key: "designation",
      render: (v) => <span style={{ color: "#64748b", fontSize: 12.5 }}>{v}</span>,
    },
    {
      title: "Status",
      dataIndex: "statusLabel",
      key: "status",
      align: "center",
      render: (v, r) => (
        <Tag color={r.statusColor} style={{ fontWeight: 700, borderRadius: 8, padding: "2px 10px" }}>
          {v}
        </Tag>
      ),
    },
    {
      title: "Check-In",
      dataIndex: "checkIn",
      key: "checkIn",
      align: "center",
      render: (v) => <span style={{ fontWeight: 600, color: v !== "-" ? "#10b981" : "#94a3b8" }}>{v}</span>,
    },
    {
      title: "Check-Out",
      dataIndex: "checkOut",
      key: "checkOut",
      align: "center",
      render: (v) => <span style={{ fontWeight: 600, color: v !== "-" ? "#2563eb" : "#94a3b8" }}>{v}</span>,
    },
    {
      title: "Total Hours",
      dataIndex: "totalTime",
      key: "totalTime",
      align: "center",
      render: (v) => (
        <Tag color="blue" style={{ borderRadius: 6, fontWeight: 700, margin: 0 }}>
          {v !== "-" ? v : "00:00"}
        </Tag>
      ),
    },
  ];

  // Export CSV
  const exportCSV = () => {
    const dateStr = selectedDate ? selectedDate.format("YYYY-MM-DD") : "today";
    const headers = ["Employee Code,Employee Name,Department,Designation,Status,Check In,Check Out,Total Hours"];
    const rows = filteredEmployees.map(
      (d) => `"${d.code}","${d.name}","${d.department}","${d.designation}","${d.statusLabel}","${d.checkIn}","${d.checkOut}","${d.totalTime}"`
    );
    const csvContent = "data:text/csv;charset=utf-8," + [headers, ...rows].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `daily_attendance_breakdown_${dateStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{
      background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
      borderRadius: 16,
      border: "1px solid #e2e8f0",
      boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
      overflow: "hidden",
    }}>
      {/* Top Accent Gradient Bar */}
      <div style={{ height: 3.5, background: "linear-gradient(90deg, #0e497b, #3b82f6)" }} />

      <div style={{ padding: "18px 20px" }}>
        {/* Header & Controls */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[12, 12]}>
          <Col>
            <Title level={4} style={{ margin: 0, fontWeight: 800, color: "#0f172a" }}>
              Employee Daily Attendance Breakdown
            </Title>
            <Text style={{ color: "#64748b", fontSize: 13 }}>
              Live real-time employee check-in logs, status compliance, and department breakdown.
            </Text>
          </Col>
          <Col>
            <Space size="middle" wrap>
              <Segmented
                options={[
                  { label: "Table View", value: "table", icon: <TableOutlined /> },
                  { label: "Pie Chart", value: "pie", icon: <PieChartOutlined /> },
                ]}
                value={viewMode}
                onChange={(v) => setViewMode(v)}
              />

              <Button
                icon={<DownloadOutlined />}
                onClick={exportCSV}
                style={{ borderRadius: 8, background: "#f1f5f9", borderColor: "#cbd5e1", fontWeight: 600 }}
              >
                Export CSV
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Filter Controls Bar */}
        <Row gutter={[10, 10]} align="middle" style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={5}>
            <DatePicker
              allowClear={false}
              value={selectedDate}
              onChange={(val) => setSelectedDate(val || dayjs())}
              style={{ width: "100%", borderRadius: 8 }}
            />
          </Col>

          <Col xs={24} sm={12} md={5}>
            <Select
              value={organisationId}
              onChange={(v) => setOrganisationId(v)}
              style={{ width: "100%" }}
            >
              <Option value="ALL">All Organisations ({organisations.length})</Option>
              {organisations.map((o) => (
                <Option key={o.id} value={o.id}>
                  {o.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Filter Department"
              value={departmentId || undefined}
              onChange={(v) => setDepartmentId(v || "")}
              allowClear
              style={{ width: "100%" }}
            >
              {departments.map((d) => (
                <Option key={d.id} value={d.id}>
                  {d.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={4}>
            <Select
              value={selectedStatus}
              onChange={(v) => setSelectedStatus(v)}
              style={{ width: "100%" }}
              suffixIcon={<FilterOutlined />}
            >
              <Option value="ALL">All Statuses ({totalCount})</Option>
              <Option value="PRESENT">Present ({presentCount})</Option>
              <Option value="LATE">Late In ({lateCount})</Option>
              <Option value="LEAVE">On Leave ({leaveCount})</Option>
              <Option value="PERMISSION">Permission ({permissionCount})</Option>
              <Option value="ABSENT">Absent ({absentCount})</Option>
              {sundayCount > 0 && <Option value="SUNDAY">Sunday/Week-Off ({sundayCount})</Option>}
              {holidayCount > 0 && <Option value="HOLIDAY">Holiday ({holidayCount})</Option>}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={5}>
            <Search
              placeholder="Search Employee / Code..."
              allowClear
              onSearch={(val) => setSearchTerm(val)}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: "100%" }}
            />
          </Col>
        </Row>

        {/* Live Summary Stat Pills */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          <Tag color="blue" style={{ borderRadius: 8, fontWeight: 700, padding: "3px 12px" }}>
            Total Staff: {totalCount}
          </Tag>
          <Tag color="green" style={{ borderRadius: 8, fontWeight: 700, padding: "3px 12px" }}>
            Present: {presentCount}
          </Tag>
          <Tag color="warning" style={{ borderRadius: 8, fontWeight: 700, padding: "3px 12px" }}>
            Late In: {lateCount}
          </Tag>
          <Tag color="error" style={{ borderRadius: 8, fontWeight: 700, padding: "3px 12px" }}>
            On Leave: {leaveCount}
          </Tag>
          <Tag color="geekblue" style={{ borderRadius: 8, fontWeight: 700, padding: "3px 12px" }}>
            Permission: {permissionCount}
          </Tag>
          <Tag color="default" style={{ borderRadius: 8, fontWeight: 700, padding: "3px 12px" }}>
            Absent: {absentCount}
          </Tag>
          {sundayCount > 0 && (
            <Tag color="cyan" style={{ borderRadius: 8, fontWeight: 700, padding: "3px 12px" }}>
              Week-Off/Sunday: {sundayCount}
            </Tag>
          )}
          {holidayCount > 0 && (
            <Tag color="purple" style={{ borderRadius: 8, fontWeight: 700, padding: "3px 12px" }}>
              Holiday: {holidayCount}
            </Tag>
          )}
        </div>

        {/* Main Content Area */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Spin size="large" />
            <div style={{ marginTop: 12, color: "#64748b", fontWeight: 600 }}>Loading Daily Attendance Breakdown...</div>
          </div>
        ) : filteredEmployees.length === 0 ? (
          <div style={{ padding: "40px 0", textAlign: "center" }}>
            <Empty description="No employee attendance logs found for selected date and filters" />
          </div>
        ) : viewMode === "pie" ? (
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie
                data={pieChartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={105}
                label={(entry) => `${entry.name}: ${entry.value}`}
              >
                {pieChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <Table
            dataSource={filteredEmployees}
            columns={tableColumns}
            rowKey={(r) => r.id || r.code}
            pagination={{ pageSize: 8, showSizeChanger: false }}
            size="small"
            scroll={{ x: 850 }}
          />
        )}
      </div>
    </div>
  );
}
